import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getTopBooks } from '../../common/Rest/Services/BookService';
import { TopBook } from '../../common/Models/Books';
import ItemGrid from '../HomePage/ItemGrid';
import { Item, ItemTypeEnum, PriceType } from '../../common/Rest/Services/Data';
import { rentalCountText } from '../../common/Utils/Utils';
import './TopBooksPage.css';

type Mode = 'all' | 'active';

const rentalLabel = (count: number, mode: Mode) =>
  mode === 'active'
    ? `${count} зараз читають`
    : rentalCountText(count);

function TopBooksPage() {
  const [mode, setMode] = useState<Mode>('all');
  const [items, setItems] = useState<TopBook[]>([]);
  const [loading, setLoading] = useState(true);
  const topItems = useMemo(
    () => items.map(({ book, rentalCount }): Item => ({
      id: book.id,
      name: book.name,
      isbn: book.isbn,
      type: ItemTypeEnum.Book,
      stockCount: book.stockCount,
      price: book.price,
      priceType: PriceType.Renatal,
      count: 1,
      image: undefined,
      description: [
        book.authors.map((author) => author.name).join(', ') || 'Автор не вказаний',
        rentalLabel(rentalCount, mode),
      ].join(' · '),
      isInWishList: false,
      primaryImage: book.primaryImage,
      images: book.images,
      series: book.series,
      seriesNumber: book.seriesNumber,
    })),
    [items, mode]
  );
  const rankBadges = useMemo(
    () => Object.fromEntries(items.map(({ book }, index) => [book.id, `#${index + 1}`])),
    [items]
  );

  useEffect(() => {
    setLoading(true);
    getTopBooks(mode === 'active', 20)
      .then(setItems)
      .catch(() => {
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [mode]);

  return (
    <div className="top-books-page default-container">
      <header className="top-books-header">
        <h1>Наші топи</h1>
        <p>Книги, які найчастіше беруть в оренду.</p>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, value: Mode | null) => value && setMode(value)}
          size="small"
        >
          <ToggleButton value="all">За весь час</ToggleButton>
          <ToggleButton value="active">Читають зараз</ToggleButton>
        </ToggleButtonGroup>
      </header>

      {loading ? (
        <CircularProgress sx={{ display: 'block', margin: '60px auto' }} />
      ) : items.length === 0 ? (
        <p className="top-books-empty">
          {mode === 'active' ? 'Зараз ніхто нічого не читає.' : 'Поки немає даних про оренди.'}
        </p>
      ) : (
        <div className="top-books-grid-shell">
          <ItemGrid
            items={topItems}
            gridColumns={4}
            lastItemRef={null}
            itemBadges={rankBadges}
            actionMode={mode === 'active' ? 'notify' : 'rent'}
            rentButtonLabel="Оренда"
          />
        </div>
      )}
    </div>
  );
}

export default TopBooksPage;
